import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Search, 
  RefreshCw, 
  ChevronUp, 
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  ArrowUpDown,
  Printer,
  Gift,
  RotateCcw,
  FileText,
  ChevronsUpDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { CustomerDetailModal } from "@/components/customers/CustomerDetailModal";
import BirthdayReminder from "@/components/BirthdayReminder";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const truncateEmail = (email, maxLen = 20) => {
  if (!email || email.length <= maxLen) return email || "";
  return email.substring(0, maxLen) + "...";
};

const PAGE_SIZE = 50;

export const CustomersV2 = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const debounceRef = useRef(null);

  // Reverse Late Penalties tool
  const [penaltySummary, setPenaltySummary] = useState(null);
  const [reversalDialogOpen, setReversalDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);

  const fetchPenaltySummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers/journals/late-penalties/summary`);
      setPenaltySummary(res.data);
    } catch (e) {
      // Non-fatal — button will just show without a count
      setPenaltySummary(null);
    }
  }, []);

  const handleOpenReversalDialog = async () => {
    await fetchPenaltySummary();
    setReversalDialogOpen(true);
  };

  const handleConfirmReversal = async () => {
    setReversing(true);
    try {
      const res = await axios.post(
        `${API}/customers/journals/late-penalties/reverse-all`,
        null,
        { params: { reason: "Bulk reversal via Customer Management", performed_by: "Admin" } }
      );
      toast.success(res.data?.message || "Late-payment penalties reversed");
      setReversalDialogOpen(false);
      fetchCustomers();
      fetchPenaltySummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reverse late penalties");
    } finally {
      setReversing(false);
    }
  };

  useEffect(() => { fetchPenaltySummary(); }, [fetchPenaltySummary]);

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setIsAddMode(true);
    setDetailModalOpen(true);
  };

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const fetchCustomers = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: debouncedSearch,
        sort_by: sortConfig.key,
        sort_dir: sortConfig.direction,
        page: page.toString(),
        page_size: PAGE_SIZE.toString(),
      });
      const { data } = await axios.get(`${API}/customers/v2/paginated?${params}`, { signal });
      setCustomers(data.customers || []);
      setTotalPages(data.total_pages || 1);
      setTotalCustomers(data.total || 0);
    } catch (error) {
      // Cancelled requests are expected when the user types fast — ignore.
      if (axios.isCancel(error) || error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sortConfig, page]);

  useEffect(() => {
    // Cancel any in-flight request from a previous keystroke / sort / page
    // change so its (potentially out-of-order) response can never overwrite
    // the fresh result. This is what was causing the "results flash, then
    // revert to all customers" bug.
    const controller = new AbortController();
    fetchCustomers(controller.signal);
    return () => controller.abort();
  }, [fetchCustomers]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
    setPage(1);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCustomers(new Set(customers.map(c => c.id)));
    } else {
      setSelectedCustomers(new Set());
    }
  };

  const handleSelectCustomer = (customerId, checked) => {
    const newSelected = new Set(selectedCustomers);
    if (checked) {
      newSelected.add(customerId);
    } else {
      newSelected.delete(customerId);
    }
    setSelectedCustomers(newSelected);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${day}-${month}-${year}, ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateStr;
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    }
    return sortConfig.direction === "asc" 
      ? <ChevronUp className="h-3 w-3 text-blue-600" /> 
      : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  const handlePrintList = () => {
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      toast.error("Unable to open print window. Please allow popups.");
      return;
    }
    const rowsHtml = customers
      .map(
        (customer, index) => `
          <tr>
            <td>${(page - 1) * PAGE_SIZE + index + 1}</td>
            <td>${customer.name || ""}</td>
            <td>${customer.contact_number || ""}</td>
            <td style="text-align:right;">${(customer.balance || 0).toFixed(2)}</td>
            <td>${customer.email || ""}</td>
            <td>${formatDateTime(customer.last_purchase)}</td>
            <td>${formatDateTime(customer.last_payment)}</td>
            <td>${customer.cust_id || ""}</td>
          </tr>
        `
      )
      .join("");
    printWindow.document.write(`
      <html>
        <head>
          <title>Customer List</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h1 { margin: 0 0 6px 0; }
            .meta { color: #555; margin-bottom: 16px; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; }
            th { background: #f3f4f6; text-align: left; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Customer List</h1>
          <div class="meta">Generated: ${new Date().toLocaleString()} &bull; Page ${page} of ${totalPages} (${totalCustomers} total)</div>
          <table>
            <thead>
              <tr><th>#</th><th>Name</th><th>Contact</th><th>Balance</th><th>Email</th><th>Last Purchase</th><th>Last Payment</th><th>CustID</th></tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <script>window.onload = () => { window.print(); };</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="h-full flex flex-col bg-gray-100" data-testid="customers-v2-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold text-gray-800">Customers</h1>
          <span className="text-sm text-gray-500">
            {totalCustomers} customer{totalCustomers !== 1 ? "s" : ""}
            {debouncedSearch && ` matching "${debouncedSearch}"`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64 h-9"
              data-testid="customer-search-input"
            />
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchCustomers()}
            disabled={loading}
            data-testid="refresh-customers-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrintList}
            data-testid="print-customer-list-btn"
          >
            <Printer className="h-4 w-4 mr-1" />
            Print List
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="download-loyalty-flyer-btn"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/loyalty/flyer/pdf`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "loyalty-programme-flyer.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                toast.error("Failed to download flyer");
              }
            }}
          >
            <Gift className="h-4 w-4 mr-1" />
            Loyalty Flyer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenReversalDialog}
            className="text-orange-700 border-orange-300 hover:bg-orange-50"
            data-testid="reverse-late-penalties-btn"
            title="Reverse all auto-applied late payment penalties"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reverse Late Penalties
            {penaltySummary?.pending_reversal_count > 0 && (
              <span className="ml-1 rounded-full bg-orange-100 text-orange-800 text-xs px-1.5 py-0.5">
                {penaltySummary.pending_reversal_count}
              </span>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="application-forms-dropdown"
                title="Print one of the account application forms"
              >
                <FileText className="h-4 w-4 mr-1" />
                Application Forms
                <ChevronsUpDown className="h-3 w-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Pick the right form</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => window.open(`${API}/customers/credit-application-form.pdf`, '_blank')}
                data-testid="form-credit-application"
              >
                <div>
                  <div className="font-medium">Credit Account</div>
                  <div className="text-xs text-gray-500">Buy now, pay end of same month</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`${API}/customers/cash-customer-form.pdf`, '_blank')}
                data-testid="form-cash-customer"
              >
                <div>
                  <div className="font-medium">Cash / Card Customer</div>
                  <div className="text-xs text-gray-500">Company info on invoices · no credit</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`${API}/customers/prepaid-customer-form.pdf`, '_blank')}
                data-testid="form-prepaid-customer"
              >
                <div>
                  <div className="font-medium">Prepaid Account</div>
                  <div className="text-xs text-gray-500">Deposit upfront, buy against balance</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-customer-btn"
            onClick={handleAddCustomer}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Reverse Late Penalties Confirmation */}
      <AlertDialog open={reversalDialogOpen} onOpenChange={setReversalDialogOpen}>
        <AlertDialogContent data-testid="reverse-late-penalties-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-700">Reverse All Late Payment Penalties</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  This will create an offsetting <strong>CREDIT journal</strong> for every
                  auto-applied <code>LATE_PAYMENT_PENALTY</code> that hasn&apos;t already been reversed.
                  The original penalty rows stay in place for the SARS audit trail.
                </p>
                {penaltySummary ? (
                  <div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
                    <div className="grid grid-cols-2 gap-y-1">
                      <span className="text-gray-600">Penalty entries in DB</span>
                      <span className="text-right font-semibold">{penaltySummary.total_penalty_count}</span>
                      <span className="text-gray-600">Customers affected</span>
                      <span className="text-right font-semibold">{penaltySummary.customers_affected}</span>
                      <span className="text-gray-600">Total penalty amount</span>
                      <span className="text-right font-semibold text-red-600">R {(penaltySummary.total_penalty_amount || 0).toFixed(2)}</span>
                      <span className="text-gray-600">Already reversed</span>
                      <span className="text-right">{penaltySummary.already_reversed_count}</span>
                      <span className="text-gray-600 border-t pt-1 mt-1">Will be reversed now</span>
                      <span className="text-right font-bold text-orange-700 border-t pt-1 mt-1">{penaltySummary.pending_reversal_count}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Loading penalty summary…</p>
                )}
                <p className="text-xs text-gray-500">
                  Auto late-penalty creation will also be <strong>switched OFF</strong> so no new penalties
                  are added going forward. You can re-enable it later in Settings.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reverse-late-penalties-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReversal}
              disabled={reversing || !penaltySummary || penaltySummary.pending_reversal_count === 0}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="reverse-late-penalties-confirm"
            >
              {reversing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reversing…</>
              ) : (
                <>Reverse {penaltySummary?.pending_reversal_count || 0} Penalties</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Birthday Reminders */}
      <div className="px-4 py-2">
        <BirthdayReminder />
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        {loading && customers.length === 0 ? (
          <div className="flex items-center justify-center h-64" data-testid="customers-loading">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
            <span className="ml-2 text-gray-500">Loading customers...</span>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Users className="h-12 w-12 mb-2 opacity-50" />
            <p>{debouncedSearch ? `No customers found matching "${debouncedSearch}"` : "No customers yet"}</p>
          </div>
        ) : (
          <Table className="bg-white">
            <TableHeader className="sticky top-0 bg-gray-200 z-10">
              <TableRow className="hover:bg-gray-200">
                <TableHead className="w-10 px-2">
                  <Checkbox 
                    checked={selectedCustomers.size === customers.length && customers.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                {[
                  { key: "name", label: "Name", className: "min-w-[200px]" },
                  { key: "contact_number", label: "Contact No." },
                  { key: "balance", label: "Balance", className: "text-right" },
                  { key: "email", label: "Email" },
                  { key: "last_purchase", label: "Last Purchase" },
                  { key: "last_payment", label: "Last Payment" },
                  { key: "cust_id", label: "CustID" },
                ].map(col => (
                  <TableHead 
                    key={col.key}
                    className={`cursor-pointer select-none font-semibold text-gray-700 ${col.className || ""}`}
                    onClick={() => handleSort(col.key)}
                    data-testid={`sort-${col.key}`}
                  >
                    <div className={`flex items-center gap-1 ${col.className?.includes("text-right") ? "justify-end" : ""}`}>
                      {col.label} <SortIcon columnKey={col.key} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer, index) => (
                <TableRow 
                  key={customer.id}
                  className={`
                    ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    hover:bg-blue-50 cursor-pointer transition-colors
                  `}
                  data-testid={`customer-row-${customer.id}`}
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setDetailModalOpen(true);
                  }}
                >
                  <TableCell className="px-2">
                    <Checkbox 
                      checked={selectedCustomers.has(customer.id)}
                      onCheckedChange={(checked) => handleSelectCustomer(customer.id, checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-gray-900">{customer.name}</TableCell>
                  <TableCell className="text-gray-600">{customer.contact_number || ""}</TableCell>
                  <TableCell className={`text-right font-mono ${
                    customer.balance > 0 ? 'text-red-600 font-semibold' : 
                    customer.balance < 0 ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {customer.balance > 0 ? '' : customer.balance < 0 ? '-' : ''}
                    {Math.abs(customer.balance || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm" title={customer.email || ""}>
                    {truncateEmail(customer.email, 22)}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm whitespace-nowrap">
                    {formatDateTime(customer.last_purchase)}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm whitespace-nowrap">
                    {formatDateTime(customer.last_payment)}
                  </TableCell>
                  <TableCell className="font-mono text-gray-600 text-sm">
                    {customer.cust_id || ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white border-t px-4 py-2 pb-14 flex items-center justify-between" data-testid="pagination-bar">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages} ({totalCustomers} total)
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(1)}
              data-testid="page-first"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => p - 1)}
              data-testid="page-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {/* Page number buttons */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 5) {
                p = i + 1;
              } else if (page <= 3) {
                p = i + 1;
              } else if (page >= totalPages - 2) {
                p = totalPages - 4 + i;
              } else {
                p = page - 2 + i;
              }
              return (
                <Button
                  key={p}
                  variant={p === page ? "default" : "outline"}
                  size="sm"
                  className={p === page ? "bg-gray-800" : ""}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
              data-testid="page-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(totalPages)}
              data-testid="page-last"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedCustomer(null);
          setIsAddMode(false);
        }}
        customer={selectedCustomer}
        isAddMode={isAddMode}
        onSaved={() => {
          fetchCustomers();
          setIsAddMode(false);
        }}
      />
    </div>
  );
};

export default CustomersV2;
